import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import { DEPTH, POSITION_SIDE } from 'src/common/constants';
import { changeScrollBehavior, currencyFormatter } from 'src/common/helpers';
import { Tooltip } from 'src/components/core';
import { Button } from 'src/components/core/button';
import { Edit, Share } from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { StyledTable } from 'src/components/features/common/table/styledTable';
import { PositionLimitCloseModal } from 'src/components/features/derivative/modals/positionLimitCloseModal';
import { PositionMarginModal } from 'src/components/features/derivative/modals/positionMarginModal';
import { PositionMarketCloseModal } from 'src/components/features/derivative/modals/positionMarketCloseModal';
import { PositionTPSLModal } from 'src/components/features/derivative/modals/positionTPSLModal';
import { ShareProfitLossModal } from 'src/components/features/derivative/modals/shareProfitLossModal';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { fetchPositionStopProfitLoss } from 'src/pages/derivatives/_redux/derivativePageActions';
import { APP_URLS } from 'src/setup/client/app-urls';
import { FutureSymbol, OrderTableContentProps, Position } from 'src/types';
import { StopProfitLossPayLoad } from 'src/types';

const StyledPositionTitle = styled.div`
    cursor: pointer;
`;

const PositionTable: FC<OrderTableContentProps> = ({ isShowAll }) => {
    const { t } = useTranslation();
    const { colors } = useTheme();
    const navigate = useNavigate();
    const [filterPositions, setFilterPositions] = useState([] as Array<Position>);
    const currentSymbol: FutureSymbol = useAppSelector(
        (state) => state.derivativePage.currentSymbol,
    );
    const symbolId = currentSymbol.symbolId;
    const { positions, positionTrigger, shortPositionStopProfitLoss, longPositionStopProfitLoss } =
        useAppSelector((state) => state.derivativePage);
    const slowBroker = useAppSelector((state) => state.socket.slowBroker);
    const [isPositionMarketCloseModalOpen, setIsPositionMarketCloseModalOpen] = useState(false);
    const [isPositionLimitCloseModalOpen, setIsPositionLimitCloseModalOpen] = useState(false);
    const [isPositionTPSLModalOpen, setIsPositionTPSLModalOpen] = useState(false);
    const [isPositionMarginModalOpen, setIsPositionMarginModalOpen] = useState(false);
    const [isProfitLossShareModalOpen, setIsProfitLossShareModalOpen] = useState(false);
    const [currentModalPosition, setCurrentModalPosition] = useState({} as Position);
    const futureSymbols = useAppSelector((state) => state.globalData.configV2.futuresSymbol);
    const dispatch = useAppDispatch();

    useEffect(() => {
        let filtered = isShowAll
            ? positions
            : positions.filter((position) => position.symbolId === symbolId);
        filtered = [...filtered].sort((a, b) => Number(b.positionId) - Number(a.positionId));
        setFilterPositions(filtered);
    }, [positions, symbolId, isShowAll]);

    useEffect(() => {
        let filtered = isShowAll
            ? positions
            : positions.filter((position) => position.symbolId === symbolId);
        filtered.forEach((position: Position) => {
            let body: StopProfitLossPayLoad = {
                symbol_id: position.symbolId,
                is_long: position.isLong,
            };
            dispatch(fetchPositionStopProfitLoss(body));
        });
    }, [positionTrigger, isShowAll, currentSymbol]);

    const openPositionMarketCloseModal = (position: Position) => {
        setCurrentModalPosition(position);
        setIsPositionMarketCloseModalOpen(!isPositionMarketCloseModalOpen);
    };

    const openPositionLimitCloseModal = (position: Position) => {
        setCurrentModalPosition(position);
        setIsPositionLimitCloseModalOpen(!isPositionLimitCloseModalOpen);
    };

    const openPositionTPSLModal = (position: Position) => {
        setCurrentModalPosition(position);
        setIsPositionTPSLModalOpen(!isPositionTPSLModalOpen);
    };

    const openPositionMarginModal = (position: Position) => {
        setCurrentModalPosition(position);
        setIsPositionMarginModalOpen(!isPositionMarginModalOpen);
    };

    const openShareProfitLossModal = (position: Position) => {
        setCurrentModalPosition(position);
        setIsProfitLossShareModalOpen(!isProfitLossShareModalOpen);
    };

    const changePair = (symbolId: string) => {
        navigate(`${APP_URLS.CONTRACT}/${symbolId}`);
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    };

    const columns = [
        {
            title: (
                <FlexContainer styles={{ alignItems: 'center' }}>{t('contracts')}</FlexContainer>
            ),
            key: 'contracts',
            width: '8%',
            render: (row: { [x: string]: any }) => {
                const tokenId = row['symbolId'].split('-')[0] as string;
                return (
                    <StyledPositionTitle
                        className={classNames(
                            'contract',
                            { borderGreen: row['isLong'] === POSITION_SIDE.LONG },
                            { borderRed: row['isLong'] === POSITION_SIDE.SHORT },
                        )}
                        onClick={() => changePair(row.symbolId)}
                    >
                        <div className="symbol-name">
                            {tokenId}
                            <span>{row['symbolName'].replace(tokenId, '')}</span>
                        </div>
                        <span className="leverage">{`${t('leverage')} ${row['leverage']}x`}</span>
                    </StyledPositionTitle>
                );
            },
        },
        {
            title: t('qty'),
            key: 'qty',
            width: '4.2%',
            render: (row: { [x: string]: any }) => {
                return (
                    <span
                        className={classNames(
                            'centerText',
                            { greenText: row['isLong'] === POSITION_SIDE.LONG },
                            { redText: row['isLong'] === POSITION_SIDE.SHORT },
                        )}
                    >
                        {currencyFormatter(
                            row['total'],
                            DEPTH[futureSymbols[row.symbolId].basePrecision],
                        )}
                    </span>
                );
            },
        },
        {
            title: t('value'),
            key: 'value',
            width: '5%',
            render: (row: { [x: string]: any }) => {
                return (
                    <span>
                        {currencyFormatter(
                            row['positionValues'],
                            DEPTH[
                                Number(futureSymbols[row.symbolId].baseTokenFutures.marginPrecision)
                            ],
                        )}
                        <br />
                        {row['unit']}
                    </span>
                );
            },
        },
        {
            title: (
                <Tooltip overlay={t('entry_price_description')} theme={'secondary'} interactive>
                    <span style={{ cursor: 'pointer' }}>{t('entry_price')}</span>
                </Tooltip>
            ),
            key: 'entryPrice',
            width: '6%',
            render: (row: { [x: string]: any }) => {
                return (
                    <span>
                        {currencyFormatter(
                            row['avgPrice'],
                            DEPTH[futureSymbols[row.symbolId].minPricePrecision],
                        )}
                    </span>
                );
            },
        },
        {
            title: t('index_price'),
            key: 'indexPrice',
            width: '6%',
            render: (row: { [x: string]: any }) => {
                return (
                    <span>
                        {currencyFormatter(
                            row['indices'],
                            DEPTH[futureSymbols[row.symbolId].minPricePrecision],
                        )}
                    </span>
                );
            },
        },
        {
            title: (
                <Tooltip overlay={t('liq_price_description')} theme={'secondary'} interactive>
                    <a href={APP_URLS.LIQ_PRICE_HELP} target="_blank" rel="noopener noreferrer">
                        {t('liq_price')}
                    </a>
                </Tooltip>
            ),
            key: 'liqPrice',
            width: '6%',
            render: (row: { [x: string]: any }) => {
                return (
                    <span className="yelloText">
                        {currencyFormatter(
                            row['liquidationPrice'],
                            DEPTH[futureSymbols[row.symbolId].minPricePrecision],
                        )}
                    </span>
                );
            },
        },
        {
            title: (
                <Tooltip overlay={t('position_margin_description')} theme={'secondary'} interactive>
                    <a
                        href={APP_URLS.POSITION_MARGIN_HELP}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t('position_margin')}
                    </a>
                </Tooltip>
            ),
            key: 'positionMargin',
            width: '10%',
            render: (row: { [x: string]: any }) => {
                return (
                    <FlexContainer>
                        <span>
                            {currencyFormatter(
                                row['margin'],
                                DEPTH[
                                    Number(
                                        futureSymbols[row.symbolId].baseTokenFutures
                                            .marginPrecision,
                                    )
                                ],
                            )}{' '}
                            {row['unit']}
                            <br />≈{currencyFormatter(row['margin'], 2)} {'USD'}
                        </span>
                        <Button
                            variant="outlined"
                            size="small"
                            className="action-button"
                            styles={{
                                marginLeft: '6px',
                                padding: '0px !important',
                                borderRadius: '3px !important',
                            }}
                            onClick={() => {
                                openPositionMarginModal(row as Position);
                            }}
                        >
                            <Edit
                                fill={colors.text.tertiary}
                                style={{ width: '16px', height: '16px' }}
                            />
                        </Button>
                    </FlexContainer>
                );
            },
        },
        {
            title: t('unrealized_pl'),
            key: 'unrealisedPnl',
            width: '10%',
            render: (row: { [x: string]: any }) => {
                const value = row['unrealisedPnl'];
                const positionSymbolId = row['symbolId'];
                const lastPrice = slowBroker[positionSymbolId]
                    ? Number(slowBroker[positionSymbolId].c)
                    : 0.0;
                const avgPrice = row['avgPrice'];
                const leverage = row['leverage'];
                const priceDiffeence =
                    row['isLong'] === POSITION_SIDE.LONG
                        ? lastPrice - avgPrice
                        : avgPrice - lastPrice;
                const profitPercentage = (priceDiffeence / avgPrice) * 100 * leverage;
                return (
                    <FlexContainer styles={{ gap: 4, alignItems: 'center', paddingRight: 8 }}>
                        <span
                            className={classNames(
                                { greenText: value >= 0 },
                                { redText: value < 0 },
                            )}
                        >
                            {`${currencyFormatter(
                                value,
                                DEPTH[
                                    Number(
                                        futureSymbols[row.symbolId].baseTokenFutures
                                            .marginPrecision,
                                    )
                                ],
                            )} ${row['unit']}`}
                            <br />
                            <span
                                style={{
                                    whiteSpace: 'normal',
                                }}
                            >
                                {`(
                                ${
                                    slowBroker[positionSymbolId]
                                        ? `${currencyFormatter(profitPercentage, 2)}%`
                                        : '--'
                                }
                                ) ≈${currencyFormatter(value, 2)} ${'USD'}`}
                            </span>
                        </span>
                        <Share
                            fill={colors.text.tertiary}
                            style={{ cursor: 'pointer', flexShrink: 0 }}
                            onClick={() => {
                                openShareProfitLossModal({
                                    ...row,
                                    profitPercentage,
                                    lastPrice,
                                } as Position);
                            }}
                        />
                    </FlexContainer>
                );
            },
        },
        {
            title: (
                <Tooltip overlay={t('realized_pnl_description')} theme={'secondary'} interactive>
                    <a href={APP_URLS.REALIZED_PL_HELP} target="_blank" rel="noopener noreferrer">
                        {t('realized_pl')}
                    </a>
                </Tooltip>
            ),
            key: 'realisedPnl',
            width: '8%',
            render: (row: { [x: string]: any }) => {
                const value = row['realisedPnl'];
                return (
                    <span className={classNames({ greenText: value >= 0 }, { redText: value < 0 })}>
                        {currencyFormatter(
                            value,
                            DEPTH[
                                Number(futureSymbols[row.symbolId].baseTokenFutures.marginPrecision)
                            ],
                        )}{' '}
                        {row['unit']}
                        <br />≈{currencyFormatter(value, 2)} {'USD'}
                    </span>
                );
            },
        },
        {
            title: t('tp_sl'),
            key: 'tpSl',
            width: '7%',
            render: (row: { [x: string]: any }) => {
                const positionSymbolId = row['symbolId'];
                const positionStopProfitLoss =
                    row['isLong'] === POSITION_SIDE.LONG
                        ? longPositionStopProfitLoss[positionSymbolId]
                        : shortPositionStopProfitLoss[positionSymbolId];
                return positionStopProfitLoss &&
                    (positionStopProfitLoss.stopProfit || positionStopProfitLoss.stopLoss) ? (
                    <FlexContainer>
                        <span>
                            <span className="greenText">
                                {positionStopProfitLoss.stopProfit
                                    ? currencyFormatter(
                                          Number(positionStopProfitLoss.stopProfitPrice),
                                          DEPTH[
                                              Number(futureSymbols[row.symbolId].minPricePrecision)
                                          ],
                                      )
                                    : '--'}
                            </span>
                            {positionStopProfitLoss.stopProfit &&
                            positionStopProfitLoss.stopLoss ? (
                                <br />
                            ) : (
                                <span className="greyText">{'/'}</span>
                            )}
                            <span className="redText">
                                {positionStopProfitLoss.stopLoss
                                    ? currencyFormatter(
                                          Number(positionStopProfitLoss.stopLossPrice),
                                          DEPTH[
                                              Number(futureSymbols[row.symbolId].minPricePrecision)
                                          ],
                                      )
                                    : '--'}
                            </span>
                        </span>
                        <Button
                            variant="outlined"
                            size="small"
                            className="action-button"
                            styles={{
                                marginLeft: '6px',
                                padding: '0px !important',
                                borderRadius: '3px !important',
                            }}
                            onClick={() => {
                                openPositionTPSLModal(row as Position);
                            }}
                        >
                            <Edit
                                fill={colors.text.tertiary}
                                style={{ width: '16px', height: '16px' }}
                            />
                        </Button>
                    </FlexContainer>
                ) : (
                    <Button
                        variant="outlined"
                        className="action-button"
                        onClick={() => {
                            openPositionTPSLModal(row as Position);
                        }}
                    >
                        {t('add')}
                    </Button>
                );
            },
        },
        {
            title: t('close_by'),
            key: 'closeBy',
            width: '8%',
            render: (row: { [x: string]: any }) => {
                return (
                    <FlexContainer>
                        <Button
                            variant="outlined"
                            className="action-button"
                            onClick={() => {
                                openPositionLimitCloseModal(row as Position);
                            }}
                        >
                            {t('limit')}
                        </Button>
                        <Button
                            variant="outlined"
                            className="action-button"
                            styles={{ marginLeft: '6px' }}
                            onClick={() => {
                                openPositionMarketCloseModal(row as Position);
                            }}
                        >
                            {t('market')}
                        </Button>
                    </FlexContainer>
                );
            },
        },
    ];
    return (
        <>
            <StyledTable columns={columns} data={filterPositions} rowKey="positionId" />
            {isPositionMarketCloseModalOpen && (
                <PositionMarketCloseModal
                    open={isPositionMarketCloseModalOpen}
                    position={currentModalPosition}
                    handleClose={() => {
                        changeScrollBehavior(!isPositionMarketCloseModalOpen);
                        setIsPositionMarketCloseModalOpen(!isPositionMarketCloseModalOpen);
                    }}
                />
            )}
            {isPositionLimitCloseModalOpen && (
                <PositionLimitCloseModal
                    open={isPositionLimitCloseModalOpen}
                    lastPrice={Number(slowBroker[currentModalPosition.symbolId].c)}
                    position={currentModalPosition}
                    handleClose={() => {
                        changeScrollBehavior(!isPositionLimitCloseModalOpen);
                        setIsPositionLimitCloseModalOpen(!isPositionLimitCloseModalOpen);
                    }}
                />
            )}
            {isPositionTPSLModalOpen && (
                <PositionTPSLModal
                    open={isPositionTPSLModalOpen}
                    position={currentModalPosition}
                    positionStopProfitLoss={
                        currentModalPosition.isLong === POSITION_SIDE.LONG
                            ? longPositionStopProfitLoss[currentModalPosition.symbolId]
                            : shortPositionStopProfitLoss[currentModalPosition.symbolId]
                    }
                    handleClose={() => {
                        changeScrollBehavior(!isPositionTPSLModalOpen);
                        setIsPositionTPSLModalOpen(!isPositionTPSLModalOpen);
                    }}
                />
            )}
            {isPositionMarginModalOpen && (
                <PositionMarginModal
                    open={isPositionMarginModalOpen}
                    position={currentModalPosition}
                    handleClose={() => {
                        changeScrollBehavior(!isPositionMarginModalOpen);
                        setIsPositionMarginModalOpen(!isPositionMarginModalOpen);
                    }}
                />
            )}
            {isProfitLossShareModalOpen && (
                <ShareProfitLossModal
                    open={isProfitLossShareModalOpen}
                    position={currentModalPosition}
                    isActive
                    handleClose={() => {
                        changeScrollBehavior(!isProfitLossShareModalOpen);
                        setIsProfitLossShareModalOpen(!isProfitLossShareModalOpen);
                    }}
                />
            )}
        </>
    );
};

export { PositionTable };
